import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { assetUrl } from '../utils/assets';
import { Howl } from 'howler';
import { sfxSpriteConfig, sfxActionMap, SFXAction } from '../config/spriteConfig';
import { fireAnalyticsEvent } from '../utils/analytics';
import { SFX_ENABLED_KEY } from '../utils/audioConsent';

interface SFXState {
  enabled: boolean;
  volume: number;
  isLoaded: boolean;
}

interface SoundEffectsContextValue {
  state: SFXState;
  toggleEnabled: () => void;
  playSound: (action: SFXAction) => void;
  playMultipleSounds: (action: SFXAction, count: number, intervalMs?: number) => void;
}

const SoundEffectsContext = createContext<SoundEffectsContextValue | undefined>(undefined);

const SFX_VOLUME_KEY = 'percy_sfx_volume';

export function SoundEffectsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SFXState>(() => {
    const savedEnabled = localStorage.getItem(SFX_ENABLED_KEY);
    const savedVolume = localStorage.getItem(SFX_VOLUME_KEY);
    return {
      enabled: savedEnabled === 'true',
      volume: savedVolume !== null ? parseFloat(savedVolume) : 0.5,
      isLoaded: false,
    };
  });

  useEffect(() => {
    const handler = () => {
      const saved = localStorage.getItem(SFX_ENABLED_KEY);
      setState(prev => ({ ...prev, enabled: saved === 'true' }));
    };
    window.addEventListener('percy-audio-consent', handler);
    return () => window.removeEventListener('percy-audio-consent', handler);
  }, []);

  const howlRef = useRef<Howl | null>(null);

  useEffect(() => {
    const spriteMap: { [key: string]: [number, number] } = {};
    for (const [name, def] of Object.entries(sfxSpriteConfig)) {
      spriteMap[name] = [def.start, def.duration];
    }

    const howl = new Howl({
      src: [assetUrl('/sounds/sfx-sprite.mp3')],
      sprite: spriteMap,
      volume: state.volume,
      preload: true,
      onload: () => {
        setState(prev => ({ ...prev, isLoaded: true }));
      },
    });

    howlRef.current = howl;

    return () => {
      howlRef.current?.unload();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (howlRef.current) {
      howlRef.current.volume(state.volume);
    }
  }, [state.volume]);

  const toggleEnabled = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.enabled;
      localStorage.setItem(SFX_ENABLED_KEY, String(newEnabled));
      fireAnalyticsEvent('audio_toggled', { channel: 'sfx', new_state: newEnabled, source: 'corner_toggle' });
      return { ...prev, enabled: newEnabled };
    });
  }, []);

  const playSound = useCallback((action: SFXAction) => {
    if (!state.enabled || !state.isLoaded || !howlRef.current) return;

    const sprites = sfxActionMap[action];
    if (!sprites || sprites.length === 0) return;

    const randomSprite = sprites[Math.floor(Math.random() * sprites.length)];
    howlRef.current.play(randomSprite);
  }, [state.enabled, state.isLoaded]);

  const playMultipleSounds = useCallback((action: SFXAction, count: number, intervalMs: number = 150) => {
    if (!state.enabled || !state.isLoaded || !howlRef.current) return;

    const sprites = sfxActionMap[action];
    if (!sprites || sprites.length === 0) return;

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        if (howlRef.current && state.enabled) {
          const randomSprite = sprites[Math.floor(Math.random() * sprites.length)];
          howlRef.current.play(randomSprite);
        }
      }, i * intervalMs);
    }
  }, [state.enabled, state.isLoaded]);

  const value: SoundEffectsContextValue = {
    state,
    toggleEnabled,
    playSound,
    playMultipleSounds,
  };

  return (
    <SoundEffectsContext.Provider value={value}>
      {children}
    </SoundEffectsContext.Provider>
  );
}

export function useSoundEffects() {
  const context = useContext(SoundEffectsContext);
  if (context === undefined) {
    throw new Error('useSoundEffects must be used within a SoundEffectsProvider');
  }
  return context;
}
